import {
    AfterViewInit,
    Component,
    HostListener,
    input,
    ViewChildren,
    QueryList,
    ElementRef,
    Inject,
    PLATFORM_ID,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { SafeResourceUrl } from '@angular/platform-browser';

import { DirectivesModule } from '@jht/brand-directives';
import {
    ComponentsModule,
    ModalModule,
    ModalService,
} from '@jht/brand-components';
import { TranslateModule } from '@jht/brand-translate';
import { BuilderComponent, BuilderField } from '@jht/brand-page-builder/core';

import { VideoData, VideoService } from '../../services/video.service';

@Component({
    selector: 'vision2-inline-banner',
    imports: [
        CommonModule,
        ComponentsModule,
        DirectivesModule,
        ModalModule,
        TranslateModule,
    ],
    templateUrl: './inline-banner.component.html',
    styleUrl: './inline-banner.component.scss',
})
@BuilderComponent({
    label: 'Inline Banner',
    category: 'Banners',
    sectionClass: '',
})
export class InlineBannerComponent implements AfterViewInit {
    backgroundImage = input<string>(); // this is the default image / desktop
    backgroundImageAltText = input<string>();
    @BuilderField({ ignore: true }) backgroundImageTablet = input<string>();
    @BuilderField({ ignore: true }) backgroundImageTabletAltText =
        input<string>();
    @BuilderField({ ignore: true }) backgroundImageMobile = input<string>();
    @BuilderField({ ignore: true }) backgroundImageMobileAltText =
        input<string>();
    buttonClass = input<string>();
    @BuilderField({ group: 'button' }) buttonText = input<string>();
    @BuilderField({ group: 'button' }) buttonUrl = input<string>();
    @BuilderField({
        type: 'color',
        options: [
            'text-bg-gray-20',
            'text-bg-gray-30',
            'text-bg-light',
            'text-bg-tertiary',
        ],
        default: 'text-bg-gray-20',
        description: 'Background and text colour of the banner.',
    })
    colorClass = input<string>();
    @BuilderField({ type: 'richtext' }) description = input<string>();
    heading = input<string>();
    headingClass = input<string>();
    @BuilderField({ description: 'Heading level rendered (h1-h5).' })
    headingValue = input<number>();
    @BuilderField({ group: 'link' }) linkText = input<string>();
    @BuilderField({ group: 'link' }) linkUrl = input<string>();
    subheading = input<string>();
    @BuilderField({
        label: 'Card position',
        optionLabels: { left: 'Left', right: 'Right' },
        description: 'Which side of the banner the text card sits on.',
    })
    type = input<'left' | 'right'>('left');
    @BuilderField({
        group: 'video',
        help: 'Vimeo video id. Shows a button that opens the video in a modal.',
    })
    modalVideoId = input<string>();
    @BuilderField({
        type: 'order',
        label: 'Actions',
        help: 'Drag to change the order these render in.',
    })
    actionOrder = input<string[]>(['video', 'button', 'link']);

    videoUrl!: VideoData;
    currentModal = '';
    videoLoaded = false;
    isBrowser: boolean = false;
    isHidden = false;

    constructor(
        private modalService: ModalService,
        private videoService: VideoService,
        @Inject(PLATFORM_ID) private platformId: Object,
    ) {
        this.isBrowser = isPlatformBrowser(this.platformId);
    }

    @HostListener('document:keydown.escape', ['$event'])
    onEscPressed(event: Event) {
        this.closeModal(this.currentModal);
    }

    @ViewChildren('inlineBannerImage')
    inlineBannerImages!: QueryList<ElementRef>;
    @ViewChildren('inlineBannerHeading')
    inlineBannerHeadings!: QueryList<ElementRef>;
    @ViewChildren('inlineBannerDescription')
    inlineBannerDescriptions!: QueryList<ElementRef>;
    @ViewChildren('inlineBannerButton')
    inlineBannerButtons!: QueryList<ElementRef>;
    @ViewChildren('inlineBannerLink')
    inlineBannerLinks!: QueryList<ElementRef>;
    @ViewChildren('inlineBannerModalLink')
    inlineBannerModalLinks!: QueryList<ElementRef>;

    ngAfterViewInit() {
        this.initializeVideos();
    }

    openModal(id: string): void {
        if (id) {
            this.currentModal = id;
            this.videoLoaded = true;
            this.modalService.open(id);
        }
    }

    closeModal(id: string): void {
        if (id) {
            this.currentModal = '';
            this.videoLoaded = false;
            this.modalService.close(id);
        }
    }

    private initializeVideos() {
        const videoInput = this.modalVideoId();
        const videoOptions = {
            autoplay: true,
            autopause: true,
            background: false,
            controls: true,
            loop: false,
            muted: false,
            responsive: false,
        };
        if (videoInput) {
            this.videoService
                .getVideoData(videoInput, { ...videoOptions })
                .then((data: any) => {
                    if (data) {
                        this.videoUrl = data;
                    }
                });
        }
    }
}
